import React, { useCallback, useState } from 'react';
import { CellPosition } from './planner.tsx';

export function usePlannerKeyboardNavigation({
  defaultColumn,
  nextColumn,
  prevColumn,
  rowCount,
  ignoreKeystrokes,
  columnCount,
}: {
  defaultColumn: number;
  nextColumn: (row: number, column: number) => number;
  prevColumn: (row: number, column: number) => number;
  rowCount: number;
  ignoreKeystrokes: boolean;
  columnCount: number;
}) {
  const defaultCell = { row: 1, column: defaultColumn };
  const [selectedCell, setSelectedCell] = useState<CellPosition>(defaultCell);
  const { row, column } = selectedCell ?? defaultCell;

  const moveDown = useCallback(() => {
    setSelectedCell({ row: (row % rowCount) + 1, column }); // 1 based
  }, [column, row, rowCount]);

  const moveUp = useCallback(() => {
    setSelectedCell({ row: ((row - 2 + rowCount) % rowCount) + 1, column }); // 1 based
  }, [column, row, rowCount]);

  const moveRight = useCallback(() => {
    if (!nextColumn) {
      return;
    }
    setSelectedCell({ row: row, column: nextColumn(row, column) });
  }, [column, nextColumn, row]);

  const moveLeft = useCallback(() => {
    if (!prevColumn) {
      return;
    }
    setSelectedCell({ row: row, column: prevColumn(row, column) });
  }, [column, prevColumn, row]);

  const onKeyDown = useCallback(
    (event: React.KeyboardEvent) => {
      if (ignoreKeystrokes) {
        return;
      }
      switch (event.key) {
        case 'ArrowDown':
          moveDown();
          event.preventDefault();
          break;
        case 'ArrowUp':
          moveUp();
          event.preventDefault();
          break;
        case 'Tab':
          if (columnCount > 1) {
            if (event.getModifierState('Shift')) {
              moveLeft();
            } else {
              moveRight();
            }
          } else {
            if (event.getModifierState('Shift')) {
              moveUp();
            } else {
              moveDown();
            }
          }
          event.preventDefault();
          break;
        case 'ArrowRight':
          if (columnCount === 1) {
            return;
          }
          moveRight();
          event.preventDefault();
          break;
        case 'ArrowLeft':
          if (columnCount === 1) {
            return;
          }
          moveLeft();
          event.preventDefault();
          break;
        default:
          return; // IMPORTANT: return early to avoid stopping propagation!
      }
    },
    [columnCount, ignoreKeystrokes, moveDown, moveLeft, moveRight, moveUp],
  );

  return { moveDown, onKeyDown, selectedCell, setSelectedCell };
}
